import { IdCard, Loader2, ScrollText } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import styles from "../../styles/applicationDetail.module.css";
import {
  formatVerifastAcceptanceStatus,
  formatVerifastIdStatus,
} from "../../utils/signaturePaymentHelpers";
import { verifastFieldBadgeVariant } from "../../utils/verifastStatusBadge";
import type { ApplicationDetailData, ApplicationLayerData } from "../../types/application";
import { useVerifastLogs } from "../../hooks/useVerifastLogs";
import { VerifastIdentityLogsModal } from "../modals/VerifastIdentityLogsModal";
import { VerifastIdentityStatusBadge } from "./VerifastIdentityStatusBadge";
import {
  GovernmentIdDocumentsSection,
  hasGovernmentIdDocuments,
} from "./GovernmentIdDocumentsSection";

const VERIFAST_ACTION_BUTTON_CLASS =
  "inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#1597ff] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:bg-slate-400";

type VerifastIdentitySectionProps = {
  applicationAllData: ApplicationDetailData;
  applicationData: ApplicationLayerData;
  applicantId: string;
  testIdPrefix?: string;
};

function readField(value: unknown): string {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "—";
  }
  return String(value);
}

export function VerifastIdentitySection({
  applicationAllData,
  applicationData,
  applicantId,
  testIdPrefix = "application-about-tab",
}: VerifastIdentitySectionProps) {
  const verifastLogs = useVerifastLogs({
    applicantId,
    hasVerifastSteps: Boolean(applicationAllData?.has_verifast_steps),
  });

  const idStatus = formatVerifastIdStatus(
    applicationData.verifast_id_verify_status ??
      applicationData.verifast_id_status,
  );
  const completionStatus = readField(applicationData.verifast_completion_status);
  const approvalStatus = formatVerifastAcceptanceStatus(
    applicationData.verifast_approval_status ??
      applicationData.verifast_acceptance_status,
  );
  const documentType = readField(applicationData.verifast_id_document_type);

  const idStatusRaw =
    applicationData.verifast_id_verify_status ?? applicationData.verifast_id_status;
  const approvalStatusRaw =
    applicationData.verifast_approval_status ??
    applicationData.verifast_acceptance_status;

  const idStatusDisplay = idStatus || "—";
  const approvalStatusDisplay = approvalStatus;
  const completionStatusDisplay = completionStatus;

  const showSection =
    Boolean(applicationAllData?.has_verifast_steps) ||
    hasGovernmentIdDocuments(applicationData) ||
    idStatus !== "" ||
    completionStatus !== "—" ||
    approvalStatus !== "Pending" ||
    documentType !== "—";

  if (!showSection) return null;

  const showStatusRow =
    Boolean(applicationAllData?.has_verifast_steps) ||
    idStatus !== "" ||
    completionStatus !== "—" ||
    approvalStatus !== "Pending" ||
    documentType !== "—";

  return (
    <>
      <div
        className={styles.section}
        data-testid={`${testIdPrefix}-section-verifast-identity`}
      >
        <h2 className={`${styles.personalInfoTitle} mb-3`}>
          <span className={styles.sectionTitleIcon} aria-hidden="true">
            <IdCard className="h-4 w-4" />
          </span>
          ID Verification
        </h2>

        {showStatusRow ? (
          <div
            className="flex flex-wrap items-stretch overflow-hidden rounded-lg border border-slate-200 bg-white"
            data-testid={`${testIdPrefix}-verifast-status-row`}
          >
            <div className="flex min-w-0 flex-1 flex-wrap divide-x divide-slate-200">
              <div className="min-w-[140px] flex-1 px-4 py-3">
                <h6 className="m-0 text-xs font-semibold text-slate-500">ID Status</h6>
                <VerifastIdentityStatusBadge
                  variant={verifastFieldBadgeVariant(idStatusRaw, idStatusDisplay, "id")}
                  label={idStatusDisplay}
                  testId={`${testIdPrefix}-verifast-id-status-badge`}
                />
              </div>
              <div className="min-w-[140px] flex-1 px-4 py-3">
                <h6 className="m-0 text-xs font-semibold text-slate-500">Completion Status</h6>
                <VerifastIdentityStatusBadge
                  variant={verifastFieldBadgeVariant(
                    applicationData.verifast_completion_status,
                    completionStatusDisplay,
                    "completion",
                  )}
                  label={completionStatusDisplay}
                  testId={`${testIdPrefix}-verifast-completion-status-badge`}
                />
              </div>
              <div className="min-w-[140px] flex-1 px-4 py-3">
                <h6 className="m-0 text-xs font-semibold text-slate-500">Approval Status</h6>
                <VerifastIdentityStatusBadge
                  variant={verifastFieldBadgeVariant(
                    approvalStatusRaw,
                    approvalStatusDisplay,
                    "approval",
                  )}
                  label={approvalStatusDisplay}
                  testId={`${testIdPrefix}-verifast-approval-status-badge`}
                />
              </div>
              <div className="min-w-[140px] flex-1 px-4 py-3">
                <h6 className="m-0 text-xs font-semibold text-slate-500">Document Type</h6>
                <VerifastIdentityStatusBadge
                  variant={verifastFieldBadgeVariant(null, documentType, "document")}
                  label={documentType}
                  testId={`${testIdPrefix}-verifast-document-type-badge`}
                />
              </div>
            </div>

            {verifastLogs.showViewLogsButton ? (
              <div
                className="flex w-full min-w-[200px] items-center justify-center border-t border-slate-200 px-4 py-3 sm:w-auto sm:max-w-[220px] sm:border-l sm:border-t-0"
                data-testid={`${testIdPrefix}-verifast-action-buttons`}
              >
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-view-verifast-logs-button`}
                  onClick={() => void verifastLogs.openVerifastLogs()}
                  disabled={!applicantId || verifastLogs.logsLoading}
                  aria-busy={verifastLogs.logsLoading}
                  className={VERIFAST_ACTION_BUTTON_CLASS}
                >
                  {verifastLogs.logsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <ScrollText className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                  View Verifast Logs
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        <GovernmentIdDocumentsSection
          applicationData={applicationData}
          applicantId={applicantId}
          testIdPrefix={testIdPrefix}
        />
      </div>

      <ConfirmDialog
        isOpen={verifastLogs.emptyLogsDialogOpen}
        title="Verifast identity logs"
        message="No Verifast events were found for this applicant."
        primaryLabel="OK"
        secondaryLabel=""
        onPrimary={() => verifastLogs.setEmptyLogsDialogOpen(false)}
        onSecondary={() => verifastLogs.setEmptyLogsDialogOpen(false)}
        onDismiss={() => verifastLogs.setEmptyLogsDialogOpen(false)}
        overlayDataTestId={`${testIdPrefix}-verifast-logs-empty`}
        primaryButtonDataTestId={`${testIdPrefix}-verifast-logs-empty-ok-button`}
        closeButtonDataTestId={`${testIdPrefix}-verifast-logs-empty-close-button`}
      />

      <VerifastIdentityLogsModal
        isOpen={verifastLogs.logsModalOpen}
        onClose={verifastLogs.closeLogsModal}
        applicantId={applicantId}
        initialSessions={verifastLogs.logSessions}
      />
    </>
  );
}
